import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

const PARTS = [
  { key: "warmup", label: "Calent." },
  { key: "main_1", label: "T1", legacy: "main" },
  { key: "main_2", label: "T2" },
  { key: "main_3", label: "T3" },
  { key: "cooldown", label: "Vuelta" },
];

const hasContent = (t) =>
  PARTS.some((p) => (t[p.key] || "").trim() || (p.legacy && (t[p.legacy] || "").trim()));

export const CopySessionPicker = ({ teamId, currentId, onCopy }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/trainings?team_id=${teamId}`)
      .then((r) => setItems((r.data || []).filter((t) => t.id !== currentId && hasContent(t))))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [open, teamId, currentId]);

  const pick = async (t) => {
    await onCopy(t);
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
        onClick={() => setOpen(true)}
        data-testid="copy-session-button"
      >
        <Copy className="h-3.5 w-3.5 mr-1.5" /> Copiar de otra sesión
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="copy-session-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-purple-600" />
              Copiar sesión de otro entrenamiento
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            Se copiarán calentamiento, tareas y vuelta a la calma (con imágenes y vídeos)
            sobre la sesión actual.
          </p>
          {loading ? (
            <div className="p-6 text-sm text-slate-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando entrenamientos...
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400 italic">
              No hay otros entrenamientos de este equipo con sesión planificada.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {items.map((t) => (
                <li key={t.id} className="p-3 flex items-center gap-3" data-testid={`copy-session-item-${t.id}`}>
                  <div className="w-9 h-9 rounded-md bg-purple-100 text-purple-700 grid place-items-center shrink-0">
                    <CalendarDays className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-sm text-slate-900">
                      {t.date}{t.time ? ` · ${t.time}` : ""}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {PARTS.filter((p) => (t[p.key] || "").trim() || (p.legacy && (t[p.legacy] || "").trim())).map((p) => (
                        <span key={p.key} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold">
                          {p.label}
                        </span>
                      ))}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => pick(t)}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold shrink-0"
                    data-testid={`copy-session-pick-${t.id}`}
                  >
                    Copiar
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
